import { toRelativeUrl } from "@okta/okta-auth-js";
import type { OktaAuth } from "@okta/okta-auth-js";
import { STORAGE_KEYS } from "../constants";

/**
 * Clear local storage and redirect to Okta login
 * Reusable authentication redirect handler
 * 
 * @param oktaAuth - Okta authentication instance
 * @param reason - Optional reason for redirect (for logging)
 */
export const redirectToOktaLogin = async (
  oktaAuth: OktaAuth,
  reason?: string
): Promise<void> => {
  // If we're currently on Okta's login callback route, do NOT start another
  // redirect cycle. Doing so can cause an infinite Okta <-> /login/callback loop.
  if (globalThis.location?.pathname?.includes("/login/callback")) {
    console.warn(
      "Skipping redirectToOktaLogin while on /login/callback to avoid redirect loop"
    );
    return;
  }

  if (reason) {
    console.log(`Redirecting to Okta login: ${reason}`);
  }

  // Do NOT clear all localStorage here. Okta Auth JS uses browser storage for
  // transaction state (PKCE, nonce/state) and wiping it can break callbacks.
  // Only clear app-owned keys.
  localStorage.removeItem(STORAGE_KEYS.AUTH_USER);
  localStorage.removeItem(STORAGE_KEYS.OKTA_TOKEN_STORAGE);
  localStorage.removeItem("persist:root");

  const originalUri = toRelativeUrl(globalThis.location.href, globalThis.location.origin);
  oktaAuth.setOriginalUri(originalUri);
  await oktaAuth.signInWithRedirect();
};
